import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useYear } from "../context/YearContext";
import YearSwitcher from "../components/YearSwitcher";
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  RefreshCw,
  Calendar,
  DollarSign,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  ShoppingCart,
  Download
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YearlyComparison = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState("production");
  const { year } = useYear();

  const fetchData = async (showToast = false) => {
    try {
      const res = await axios.get(`${API}/analytics/yearly-comparison?year=${year}`);
      setData(res.data);
      setLastUpdate(new Date());
      if (showToast) {
        toast.success("Datos actualizados");
      }
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      if (showToast) {
        toast.error("Error al actualizar datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      toast.info("Sincronizando datos de facturación... Esto puede tardar unos minutos.");
      const res = await axios.post(`${API}/analytics/sync-monthly-revenue`);
      if (res.data.success) {
        toast.success(`Sincronizados ${res.data.updated_months} meses de datos`);
        fetchData();
      }
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Error al sincronizar datos");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(false), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const DiffBadge = ({ value, suffix = "%" }) => {
    if (value === 0 || value === undefined) return <Badge variant="secondary"><Minus className="w-3 h-3 mr-1" />0{suffix}</Badge>;
    if (value > 0) return <Badge className="bg-emerald-500"><TrendingUp className="w-3 h-3 mr-1" />+{value.toFixed(1)}{suffix}</Badge>;
    return <Badge variant="destructive"><TrendingDown className="w-3 h-3 mr-1" />{value.toFixed(1)}{suffix}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const production = data?.production;
  const billing = data?.billing;
  const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const AnnualHero = ({ section, title, icon: Icon, accentColor }) => {
    const cy = data?.current_year;
    const py = data?.previous_year;
    const cyTotal = section?.totals?.current_revenue || 0;
    const pyTotal = section?.totals?.previous_revenue || 0;
    const cyNights = section?.totals?.current_room_nights || 0;
    const pyNights = section?.totals?.previous_room_nights || 0;
    const diffPct = section?.totals?.diff_percent || 0;
    const diffAbs = cyTotal - pyTotal;
    const isPositive = diffAbs >= 0;

    // Chart data: one bar per year
    const annualChart = [
      { year: String(py), value: pyTotal, nights: pyNights, fill: "#94a3b8" },
      { year: String(cy), value: cyTotal, nights: cyNights, fill: accentColor },
    ];

    return (
      <Card className="border-2" data-testid={`annual-hero-${title.toLowerCase().split(' ')[0]}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: accentColor }} />
            <CardTitle>Comparativa Anual: {title}</CardTitle>
          </div>
          <CardDescription>
            Total {cy} vs total {py} (acumulado por año)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Big numbers side */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg p-4" style={{ backgroundColor: `${accentColor}15` }}>
                  <p className="text-xs uppercase font-semibold" style={{ color: accentColor }}>
                    Total {cy}
                  </p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 break-all" style={{ color: accentColor }} data-testid={`annual-current-${title.toLowerCase().split(' ')[0]}`}>
                    €{cyTotal.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {cyNights.toLocaleString("es-ES")} estancias · {section?.totals?.current_months_count || 0} meses
                  </p>
                </div>
                <div className="bg-muted/40 rounded-lg p-4">
                  <p className="text-xs uppercase font-semibold text-muted-foreground">
                    Total {py}
                  </p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 text-muted-foreground break-all">
                    €{pyTotal.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {pyNights.toLocaleString("es-ES")} estancias · {section?.totals?.previous_months_count || 0} meses
                  </p>
                </div>
              </div>
              <div
                className={`rounded-lg p-4 border-2 ${isPositive ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
              >
                <p className="text-xs uppercase font-semibold text-muted-foreground">
                  Variación anual {cy} vs {py}
                </p>
                <div className="flex flex-wrap items-baseline gap-2 sm:gap-3 mt-2">
                  <p className={`text-3xl sm:text-4xl font-bold ${isPositive ? "text-emerald-600" : "text-red-600"}`}>
                    {isPositive ? "+" : ""}{diffPct.toFixed(1)}%
                  </p>
                  <p className={`text-base sm:text-lg font-semibold break-all ${isPositive ? "text-emerald-600" : "text-red-600"}`}>
                    ({isPositive ? "+" : ""}€{diffAbs.toLocaleString("es-ES", { maximumFractionDigits: 0 })})
                  </p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {section?.totals?.current_months_count || 0} meses comparados vs {section?.totals?.previous_months_count || 0} meses año anterior
                </p>
              </div>
            </div>

            {/* Chart side */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={annualChart} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="year" tick={{ fontSize: 14, fontWeight: 600 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(v) => `€${Number(v).toLocaleString("es-ES")}`}
                    labelFormatter={(label) => `Año ${label}`}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const SummaryCards = ({ section, title, icon: Icon, color }) => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} />
        {title}
      </h3>
      
      {/* YTD Cards */}
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Comparativa YTD (Ene - {months[data?.current_month - 1]})</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{data?.current_year} (YTD)</span>
                <DollarSign className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-lg font-bold">€{section?.ytd?.current_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</p>
              <DiffBadge value={section?.ytd?.diff_percent} />
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{data?.previous_year} (YTD)</span>
                <DollarSign className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-muted-foreground">€{section?.ytd?.previous_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Estancias {data?.current_year}</span>
                <Calendar className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-lg font-bold">{section?.ytd?.current_room_nights?.toLocaleString('es-ES')}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Estancias {data?.previous_year}</span>
                <Calendar className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-muted-foreground">{section?.ytd?.previous_room_nights?.toLocaleString('es-ES')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Total Year Cards */}
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Año Completo ({section?.totals?.current_months_count || 0} meses vs {section?.totals?.previous_months_count || 0} meses)</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Total {data?.current_year}</span>
                <DollarSign className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-lg font-bold text-blue-600">€{section?.totals?.current_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</p>
              <span className="text-xs text-muted-foreground">({section?.totals?.current_months_count || 0} meses)</span>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Total {data?.previous_year}</span>
                <DollarSign className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-muted-foreground">€{section?.totals?.previous_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</p>
              <span className="text-xs text-muted-foreground">({section?.totals?.previous_months_count || 0} meses)</span>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Estancias {data?.current_year}</span>
                <Calendar className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-lg font-bold text-blue-600">{section?.totals?.current_room_nights?.toLocaleString('es-ES')}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Estancias {data?.previous_year}</span>
                <Calendar className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-muted-foreground">{section?.totals?.previous_room_nights?.toLocaleString('es-ES')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Average Monthly Cards - NEW */}
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Media Mensual</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Media {data?.current_year}</span>
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-lg font-bold text-emerald-600">€{section?.average?.current_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</p>
              <DiffBadge value={section?.average?.diff_percent} />
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Media {data?.previous_year}</span>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-muted-foreground">€{section?.average?.previous_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</p>
            </CardContent>
          </Card>
          
          <Card className="border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Estancias/mes {data?.current_year}</span>
                <Calendar className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-lg font-bold text-emerald-600">{section?.average?.current_room_nights?.toLocaleString('es-ES')}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Estancias/mes {data?.previous_year}</span>
                <Calendar className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-muted-foreground">{section?.average?.previous_room_nights?.toLocaleString('es-ES')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  const MonthlyChart = ({ monthlyData, title }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} barGap={0}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" className="text-xs" />
              <YAxis className="text-xs" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
              <Tooltip 
                formatter={(value) => [`€${value?.toLocaleString('es-ES', {minimumFractionDigits: 2})}`, ""]}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar 
                dataKey="current_revenue" 
                name={`${data?.current_year}`} 
                fill="#3b82f6" 
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="previous_revenue" 
                name={`${data?.previous_year}`} 
                fill="#94a3b8" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );

  const MonthlyTable = ({ monthlyData }) => (
    <Card>
      <CardHeader>
        <CardTitle>Detalle Mensual</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-2 font-medium">Mes</th>
              <th className="text-right py-3 px-2 font-medium">{data?.current_year}</th>
              <th className="text-right py-3 px-2 font-medium">{data?.previous_year}</th>
              <th className="text-center py-3 px-2 font-medium">Dif. %</th>
              <th className="text-right py-3 px-2 font-medium hidden sm:table-cell">Est. {data?.current_year}</th>
              <th className="text-right py-3 px-2 font-medium hidden sm:table-cell">Est. {data?.previous_year}</th>
              <th className="text-center py-3 px-2 font-medium hidden md:table-cell">Ocup. {data?.current_year}</th>
              <th className="text-center py-3 px-2 font-medium hidden md:table-cell">Ocup. {data?.previous_year}</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData?.map((month) => (
              <tr key={month.month_num} className="border-b last:border-0 hover:bg-muted/50">
                <td className="py-3 px-2 font-medium">{month.month}</td>
                <td className="text-right py-3 px-2">
                  <span className="font-mono">€{month.current_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</span>
                </td>
                <td className="text-right py-3 px-2">
                  <span className="font-mono text-muted-foreground">€{month.previous_revenue?.toLocaleString('es-ES', {minimumFractionDigits: 2})}</span>
                </td>
                <td className="text-center py-3 px-2">
                  {month.previous_revenue > 0 ? (
                    month.revenue_diff_percent > 0 ? (
                      <span className="text-emerald-600 flex items-center justify-center gap-1">
                        <ArrowUpRight className="w-4 h-4" />
                        +{month.revenue_diff_percent?.toFixed(1)}%
                      </span>
                    ) : month.revenue_diff_percent < 0 ? (
                      <span className="text-red-600 flex items-center justify-center gap-1">
                        <ArrowDownRight className="w-4 h-4" />
                        {month.revenue_diff_percent?.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground">0%</span>
                    )
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="text-right py-3 px-2 font-mono hidden sm:table-cell">
                  {month.current_room_nights?.toLocaleString('es-ES')}
                </td>
                <td className="text-right py-3 px-2 font-mono text-muted-foreground hidden sm:table-cell">
                  {month.previous_room_nights?.toLocaleString('es-ES')}
                </td>
                <td className="text-center py-3 px-2 hidden md:table-cell">
                  {month.current_occupancy > 0 ? (
                    <span className="font-mono font-semibold" style={{
                      color: month.current_occupancy >= 80 ? "#059669" : month.current_occupancy >= 50 ? "#ca8a04" : "#dc2626"
                    }}>
                      {month.current_occupancy?.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="text-center py-3 px-2 hidden md:table-cell">
                  {month.previous_occupancy > 0 ? (
                    <span className="font-mono text-muted-foreground">
                      {month.previous_occupancy?.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 font-bold bg-muted/30">
              <td className="py-3 px-2">TOTAL</td>
              <td className="text-right py-3 px-2 text-blue-600">
                €{monthlyData?.reduce((sum, m) => sum + (m.current_revenue || 0), 0).toLocaleString('es-ES', {minimumFractionDigits: 2})}
              </td>
              <td className="text-right py-3 px-2 text-muted-foreground">
                €{monthlyData?.reduce((sum, m) => sum + (m.previous_revenue || 0), 0).toLocaleString('es-ES', {minimumFractionDigits: 2})}
              </td>
              {(() => {
                const totCur = monthlyData?.reduce((s, m) => s + (m.current_revenue || 0), 0) || 0;
                const totPrev = monthlyData?.reduce((s, m) => s + (m.previous_revenue || 0), 0) || 0;
                const diff = totPrev > 0 ? ((totCur - totPrev) / totPrev) * 100 : 0;
                return (
                  <td className="text-center py-3 px-2">
                    {totPrev > 0 ? (
                      diff > 0 ? (
                        <span className="text-emerald-600 flex items-center justify-center gap-1">
                          <ArrowUpRight className="w-4 h-4" />+{diff.toFixed(1)}%
                        </span>
                      ) : diff < 0 ? (
                        <span className="text-red-600 flex items-center justify-center gap-1">
                          <ArrowDownRight className="w-4 h-4" />{diff.toFixed(1)}%
                        </span>
                      ) : <span className="text-muted-foreground">0%</span>
                    ) : <span className="text-muted-foreground">-</span>}
                  </td>
                );
              })()}
              <td className="text-right py-3 px-2 hidden sm:table-cell text-blue-600">
                {monthlyData?.reduce((sum, m) => sum + (m.current_room_nights || 0), 0).toLocaleString('es-ES')}
              </td>
              <td className="text-right py-3 px-2 hidden sm:table-cell text-muted-foreground">
                {monthlyData?.reduce((sum, m) => sum + (m.previous_room_nights || 0), 0).toLocaleString('es-ES')}
              </td>
              {(() => {
                // Media ponderada de ocupación por días del mes (solo meses con datos)
                const dpm = [31,28,31,30,31,30,31,31,30,31,30,31];
                let dC=0, sC=0, dP=0, sP=0;
                (monthlyData || []).forEach((m) => {
                  const i = (m.month_num || 1) - 1;
                  if ((m.current_occupancy || 0) > 0) { dC += dpm[i]; sC += (m.current_occupancy || 0) * dpm[i]; }
                  if ((m.previous_occupancy || 0) > 0) { dP += dpm[i]; sP += (m.previous_occupancy || 0) * dpm[i]; }
                });
                const avgC = dC > 0 ? sC / dC : 0;
                const avgP = dP > 0 ? sP / dP : 0;
                return (
                  <>
                    <td className="text-center py-3 px-2 hidden md:table-cell">
                      {avgC > 0 ? (
                        <span className="font-mono font-semibold" style={{
                          color: avgC >= 80 ? "#059669" : avgC >= 50 ? "#ca8a04" : "#dc2626"
                        }}>{avgC.toFixed(1)}%</span>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="text-center py-3 px-2 hidden md:table-cell">
                      {avgP > 0 ? (
                        <span className="font-mono text-muted-foreground">{avgP.toFixed(1)}%</span>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                  </>
                );
              })()}
            </tr>
          </tfoot>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="yearly-comparison-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Comparativa Anual</h1>
          <p className="text-muted-foreground">
            {data?.current_year} vs {data?.previous_year}
            {lastUpdate && (
              <span className="ml-2 text-xs">
                · Actualizado {lastUpdate.toLocaleTimeString('es-ES')}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <YearSwitcher />
          <Button variant="outline" onClick={() => fetchData(true)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const token = localStorage.getItem("token");
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/yearly-comparison/excel`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `comparativa_anual_${data?.current_year}_vs_${data?.previous_year}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Excel descargado");
              } catch (e) {
                toast.error("Error al exportar Excel");
              }
            }}
            data-testid="export-yearly-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const token = localStorage.getItem("token");
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/yearly-comparison/pdf`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `comparativa_anual_${data?.current_year}_vs_${data?.previous_year}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("PDF descargado");
              } catch (e) {
                toast.error("Error al exportar PDF");
              }
            }}
            data-testid="export-yearly-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={async () => {
              try {
                const token = localStorage.getItem("token");
                const res = await axios.get(
                  `${process.env.REACT_APP_BACKEND_URL}/api/reports/kpi-production-pdf?year=${year}`,
                  { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
                );
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `kpi_produccion_${year}_vs_${year - 1}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success(`KPI Producción ${year} vs ${year - 1} descargado`);
              } catch (e) {
                toast.error("Error al descargar KPI PDF");
              }
            }}
            data-testid="export-kpi-production-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            ADR/Ocup/RevPAR
          </Button>
          <Button onClick={handleSync} disabled={syncing} className="hidden sm:flex">
            {syncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Calendar className="w-4 h-4 mr-2" />
            )}
            Sincronizar Oscar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="production" className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            Producción (Reservas)
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Facturación (Estancias)
          </TabsTrigger>
        </TabsList>

        {/* Tab Producción */}
        <TabsContent value="production" className="space-y-6">
          <AnnualHero
            section={production}
            title="Producción"
            icon={ShoppingCart}
            accentColor="#3b82f6"
          />
          <SummaryCards 
            section={production} 
            title="Producción (Reservas realizadas)"
            icon={ShoppingCart}
            color="text-blue-500"
          />
          <MonthlyChart 
            monthlyData={production?.monthly} 
            title="Producción por Mes"
          />
          <MonthlyTable monthlyData={production?.monthly} />
        </TabsContent>

        {/* Tab Facturación */}
        <TabsContent value="billing" className="space-y-6">
          <AnnualHero
            section={billing}
            title="Facturación"
            icon={FileText}
            accentColor="#10b981"
          />
          <SummaryCards 
            section={billing} 
            title="Facturación (Estancias realizadas)"
            icon={FileText}
            color="text-green-500"
          />
          <MonthlyChart 
            monthlyData={billing?.monthly} 
            title="Facturación por Mes"
          />
          <MonthlyTable monthlyData={billing?.monthly} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default YearlyComparison;
